import React, { useEffect, useRef, useState } from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";

const COLORS = ["#dc2626", "#2563eb", "#16a34a", "#111827", "#f59e0b"];

const eyeSvg = (label) =>
    `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
  <rect width="400" height="300" fill="#ffffff"/>
  <path d="M8 150 Q200 -20 392 150 Q200 320 8 150 Z" fill="#f8fafc" stroke="#475569" stroke-width="3"/>
  <circle cx="200" cy="150" r="62" fill="#e0e7ff" stroke="#475569" stroke-width="3"/>
  <circle cx="200" cy="150" r="26" fill="#1e293b"/>
  <circle cx="188" cy="140" r="7" fill="#ffffff" opacity=".85"/>
  <text x="20" y="30" font-family="sans-serif" font-size="18" fill="#64748b">${label}</text>
</svg>`)}`;

function EyeCanvas({ title, label, value, onChange, tool, canvasRef }) {
    const loadedRef = useRef(false);
    const cursorRef = useRef(null);
    const size = tool.erase ? tool.width * 3 : tool.width;

    const moveCursor = (e) => {
        const box = e.currentTarget.getBoundingClientRect();
        const c = cursorRef.current;
        c.style.display = "block";
        c.style.transform = `translate(${e.clientX - box.left - size / 2}px, ${e.clientY - box.top - size / 2}px)`;
    };

    // ponytail: paths load once on mount; refresh the tab to reload from the server
    useEffect(() => {
        if (!loadedRef.current && value?.length) {
            canvasRef.current?.loadPaths(value);
        }
        loadedRef.current = true;
    }, [value]);

    useEffect(() => {
        canvasRef.current?.eraseMode(tool.erase);
    }, [tool.erase]);

    // exportPaths() runs on stroke end only, not on every mouse move
    const sync = async () => {
        const paths = await canvasRef.current.exportPaths();
        console.log(`[eyeDrawing] ${label}`, paths, JSON.stringify(paths).length, "bytes");
        onChange(paths);
    };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                    {title}
                </h3>
                <div className="flex gap-2 text-sm">
                    <button type="button" className="rounded-lg border px-2 py-1 dark:text-white" onClick={() => canvasRef.current.undo()}>
                        Deshacer
                    </button>
                    <button type="button" className="rounded-lg border px-2 py-1 dark:text-white" onClick={() => canvasRef.current.redo()}>
                        Rehacer
                    </button>
                    <button
                        type="button"
                        className="rounded-lg border px-2 py-1 text-red-600"
                        onClick={async () => {
                            canvasRef.current.clearCanvas();
                            onChange([]);
                        }}
                    >
                        Limpiar
                    </button>
                </div>
            </div>
            <div
                className="relative"
                style={{ cursor: "none" }}
                onPointerMove={moveCursor}
                onPointerLeave={() => (cursorRef.current.style.display = "none")}
            >
                <div
                    ref={cursorRef}
                    aria-hidden="true"
                    className="pointer-events-none absolute left-0 top-0 z-10 rounded-full"
                    style={{
                        display: "none",
                        width: size,
                        height: size,
                        background: tool.erase ? "rgba(255,255,255,.6)" : tool.color,
                        border: tool.erase ? "1px solid #0f172a" : "1px solid rgba(255,255,255,.8)",
                    }}
                />
            <ReactSketchCanvas
                ref={canvasRef}
                width="100%"
                height="max(520px, 75vh)"
                backgroundImage={eyeSvg(label)}
                preserveBackgroundImageAspectRatio="xMidYMid meet"
                strokeColor={tool.color}
                strokeWidth={tool.width}
                eraserWidth={tool.width * 3}
                canvasColor="#ffffff"
                exportWithBackgroundImage
                onStroke={sync}
                style={{ borderRadius: "0.75rem", border: "1px solid #cbd5e1", touchAction: "none" }}
            />
            </div>
        </div>
    );
}

export default function EyeDrawingTab({ consultation, handleChange }) {
    const [tool, setTool] = useState({ color: COLORS[0], width: 3, erase: false });
    const drawing = consultation.eyeDrawing || {};
    const rightRef = useRef(null);
    const leftRef = useRef(null);

    const update = (field) => (paths) =>
        handleChange("eyeDrawing", {
            ...(consultation.eyeDrawing || {}),
            [field]: paths,
        });

    return (
        <section className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 sm:p-5">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                Dibujo ocular
            </h2>
            <p className="mb-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                Dibujá sobre el esquema de cada ojo.
            </p>

            <div className="mb-4 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                <div className="flex items-center gap-2">
                    {COLORS.map((c) => (
                        <button
                            key={c}
                            type="button"
                            aria-label={`Color ${c}`}
                            onClick={() => setTool((t) => ({ ...t, color: c, erase: false }))}
                            className="h-7 w-7 rounded-full border-2"
                            style={{
                                background: c,
                                borderColor: !tool.erase && tool.color === c ? "#0f172a" : "transparent",
                                outline: !tool.erase && tool.color === c ? "2px solid #94a3b8" : "none",
                            }}
                        />
                    ))}
                    <input
                        type="color"
                        aria-label="Color personalizado"
                        value={tool.color}
                        onChange={(e) => setTool((t) => ({ ...t, color: e.target.value, erase: false }))}
                        className="h-7 w-9 cursor-pointer"
                    />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                    Grosor
                    <input
                        type="range"
                        min="1"
                        max="20"
                        value={tool.width}
                        onChange={(e) => setTool((t) => ({ ...t, width: Number(e.target.value) }))}
                    />
                    <span className="w-6 text-right">{tool.width}</span>
                </label>
                <button
                    type="button"
                    onClick={() => setTool((t) => ({ ...t, erase: !t.erase }))}
                    className={`rounded-lg border px-3 py-1 text-sm ${
                        tool.erase ? "bg-slate-900 text-white" : "dark:text-white"
                    }`}
                >
                    Borrador
                </button>
            </div>

            <div className="grid gap-6">
                <EyeCanvas
                    title="Ojo derecho (OD)"
                    label="OD"
                    value={drawing.rightEyeStrokes}
                    onChange={update("rightEyeStrokes")}
                    tool={tool}
                    canvasRef={rightRef}
                />
                <EyeCanvas
                    title="Ojo izquierdo (OI)"
                    label="OI"
                    value={drawing.leftEyeStrokes}
                    onChange={update("leftEyeStrokes")}
                    tool={tool}
                    canvasRef={leftRef}
                />
            </div>
        </section>
    );
}
